import { useCallback, useContext, useEffect } from 'react';

import ProgressiveImage from '../../ProgressiveImage';

import { ImageContext } from '@contexts/image';
import { VideoContext } from '@contexts/video';
import { BandStateContext } from '@contexts/band';

import css from '@styles/productDetail/thumbnails';

import type { TBandDetail } from '@types/band';
import type { ObjectType } from '@types/misc';

type TImageThumbnailProps = {
  image: {
    thumbnail: string,
    standard: string,
    zoom: string
  },
  style: ObjectType
};

type TTVideoThumbnailProps = {
  video: string
};

const play = '/images/play-video.svg';

function ImageThumbnail({ image }: TImageThumbnailProps) {
  const { image: activeImage, setImage } = useContext(ImageContext);
  const { clearVideo } = useContext(VideoContext);

  const handleClick = useCallback(() => {
    setImage(image);
    clearVideo();
  }, [image]);

  return (
    <MemoizedImageThumbnail
      url={image.thumbnail}
      onClick={handleClick}
      active={image.standard === activeImage.standard}
    />
  );
}

const MemoizedImageThumbnail = React.memo(function({ url, onClick, active }) {
  return (
    <div
      className={`${css.thumbnail} ${active ? 'active' : ''}`}
      onClick={onClick}
    >
      <div className={css.thumbnailImageContainer}>
        <div className={`${css.thumbnailImageBox} thumbnail--image-box`}>
          <ProgressiveImage
            images={[
              {
                src: url,
                className: css.thumbnailImage,
                alt: 'Thumbnail'
              }
            ]}
            loadingClassName={css.thumbnailImage}
          />
        </div>
      </div>
    </div>
  );
});

function VideoThumbnail({ video }: TTVideoThumbnailProps) {
  const { video: activeVideo, setVideo } = useContext(VideoContext);
  const { setImage } = useContext(ImageContext);

  const handleClick = useCallback(() => {
    setVideo({
      url: video,
      state: activeVideo.state === 'play' ? 'pause' : 'play'
    });
    setImage({});
  }, [video, activeVideo.state]);

  return (
    <div
      className={`${css.thumbnail} ${
        video === activeVideo.url ? 'active' : ''
      }`}
      onClick={handleClick}
    >
      <div className={css.thumbnailImageContainer}>
        <div className={`${css.thumbnailImageBox} thumbnail--image-box`}>
          <ProgressiveImage
            images={[
              {
                src: play,
                className: `${css.thumbnailImage} video`,
                alt: 'Thumbnail'
              }
            ]}
            loadingClassName={css.thumbnailImage}
          />
        </div>
      </div>
    </div>
  );
}

export default function ImageThumbnails({
  style = {}
}: {
  style?: ObjectType
}) {
  const { band }: { band: TBandDetail } = useContext(BandStateContext);
  const { clearVideo } = useContext(VideoContext);

  const displayableImages = (band.images || []).filter(image => image.display);

  const firstImage = displayableImages[0];

  useEffect(() => {
    // If a video is playing and we switch to an "alternate metal" band, the
    // video for the previous band keeps playing. If we are changing the
    // images, then we can assume we are changing the displayed band and
    // should stop any video that may be playing.
    clearVideo();
  }, [firstImage]);

  if (!(band && displayableImages.length)) return null;
  const video = band.video && (band.video.DownloadUrl || band.video.Url);

  return (
    <div className={css.container} style={style}>
      {displayableImages.map((image, i) => {
        if (i >= (video ? 4 : 5)) return null;

        return <ImageThumbnail image={image} key={i} />;
      })}

      {video && <VideoThumbnail video={video} />}
    </div>
  );
}
